import { isAxiosError } from "axios"

import type {
  ControlledSource,
  SourceDescriptor,
  SourceSettings,
} from "@/api/filth-source-control-api"
import type { FilthSource } from "@/api/filth-sources-api"

export const sourceControlKey = "adminDashboard.filthBackends.sourceControl"
const errorCodes = [
  "capability_denied",
  "service_required",
  "credential_required",
  "body_too_large",
  "invalid_request",
  "control_plane_unavailable",
  "audit_unavailable",
  "source_kind_unavailable",
  "app_not_configured",
  "revision_required",
  "invalid_revision",
  "invalid_settings",
  "interval_below_ceiling",
  "invalid_source_identity",
  "root_outside_ceiling",
  "root_unreadable",
  "acl_mode_unavailable",
  "invalid_idempotency_key",
  "confirmation_mismatch",
  "invalid_query",
  "source_overlap",
  "invalid_action",
  "immutable_field",
  "source_in_use",
  "revision_conflict",
  "sync_unavailable",
  "source_busy",
  "permission_denied",
  "source_paused",
  "source_exists",
  "source_not_found",
  "stale_revision",
  "precondition_required",
  "invalid_value",
  "out_of_bounds",
  "site_unreachable",
  "app_not_found",
  "path_not_allowed",
  "path_not_found",
  "root_overlap",
  "unsupported_acl_mode",
  "unsupported",
  "idempotency_conflict",
  "rate_limited",
  "source_retired",
  "invalid_cursor",
  "source_bound_to_libraries",
  "admin_credential_missing",
  "credential_error",
  "unreachable",
  "unavailable",
  "invalid_response",
]
export function sourceControlError(error: unknown): string {
  const code: unknown = isAxiosError(error) ? error.response?.data?.error : undefined
  return typeof code === "string" && errorCodes.includes(code) ? code : "filth_error"
}
export function boundLibrariesError(error: unknown): { id: string; name: string }[] {
  const value: unknown = isAxiosError(error) ? error.response?.data?.libraries : undefined
  return Array.isArray(value)
    ? value.filter((v) => typeof v?.id === "string" && typeof v?.name === "string")
    : []
}
export function controlledSource(source: FilthSource): ControlledSource | null {
  return source.uid && source.revision && source.settings && source.status && source.bound_libraries
    ? ({ ...source, target: source.control_target ?? source.target } as ControlledSource)
    : null
}
export function pathUnderRoots(path: string, roots: string[]) {
  return (
    path.startsWith("/") &&
    !path.includes("\\") &&
    !path.split("/").some((v) => v === "." || v === "..") &&
    roots.some((r) => path === r.replace(/\/$/, "") || path.startsWith(`${r.replace(/\/$/, "")}/`))
  )
}
export function validSourceSettings(
  settings: SourceSettings,
  ceilings: SourceDescriptor["ceilings"]
) {
  const { max_file_bytes: max, min_interval_seconds: min } = ceilings
  return (
    Number.isSafeInteger(settings.max_file_bytes) &&
    settings.max_file_bytes > 0 &&
    settings.max_file_bytes <= max &&
    (settings.interval_seconds === null ||
      (Number.isSafeInteger(settings.interval_seconds) &&
        settings.interval_seconds >= min &&
        settings.interval_seconds <= 2147483647)) &&
    settings.include_extensions.length > 0 &&
    settings.include_extensions.length <= 64 &&
    new Set(settings.include_extensions).size === settings.include_extensions.length &&
    settings.include_extensions.every((v) => /^[a-z0-9]{1,10}$/.test(v))
  )
}

export function sourceTargetLabel(target: ControlledSource["target"]): string {
  return typeof target === "string" ? target : Object.values(target).filter(Boolean).join(" / ")
}
export function editableSourceSettings(settings: SourceSettings): Omit<SourceSettings, "acl_mode"> {
  return {
    include_extensions: settings.include_extensions,
    max_file_bytes: settings.max_file_bytes,
    interval_seconds: settings.interval_seconds,
  }
}

// Decimal MB, matching the 25,000,000-byte deployment ceiling (25 MB).
export const BYTES_PER_MB = 1_000_000

export function bytesToMb(bytes: number): number {
  return bytes / BYTES_PER_MB
}

export function mbToBytes(mb: number): number {
  return Math.round(mb * BYTES_PER_MB)
}

const SITE_PATH_PREFIXES = ["/sites/", "/teams/", "/personal/"]

export type SourceTargetDisplay = { primary: string; secondary: string | null; raw: string }

function truncateMiddle(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, Math.max(0, max - 1))}…` : value
}

/**
 * Turns a raw FILTH source target (a SharePoint/OneDrive site+library
 * composite, a filesystem path, or an opaque feed identifier) into a
 * friendly primary label plus a muted secondary line, keeping the raw
 * value available for a tooltip/copy affordance. Falls back to the raw
 * value (truncated) for anything it cannot confidently parse.
 */
export function sourceTargetDisplay(target: string): SourceTargetDisplay {
  const raw = typeof target === "string" ? target : ""
  if (!raw) return { primary: "—", secondary: null, raw }
  // SharePoint/OneDrive targets are joined by the backend with " / " —
  // a marker that never appears inside a plain filesystem path.
  if (raw.includes(" / ")) {
    const parts = raw.split(" / ")
    const siteIndex = parts.findIndex((part) =>
      SITE_PATH_PREFIXES.some((prefix) => part.startsWith(prefix))
    )
    if (siteIndex > 0) {
      const host = parts[0].split(",")[0].trim()
      const segments = parts[siteIndex].split("/").filter(Boolean)
      const siteName = segments.at(-1)
      if (host && siteName) return { primary: siteName, secondary: host, raw }
    }
    return { primary: truncateMiddle(raw, 60), secondary: null, raw }
  }
  if (raw.includes("/")) {
    const segments = raw.split("/").filter(Boolean)
    const primary = segments.at(-1)
    if (primary) {
      const secondary = segments.length > 1 ? `/${segments.slice(0, -1).join("/")}` : null
      return { primary: truncateMiddle(primary, 60), secondary, raw }
    }
  }
  return { primary: truncateMiddle(raw, 60), secondary: null, raw }
}
